/**
 * Hook for handling background removal for video overlays
 * 
 * This hook manages:
 * - Uploading videos to Convex storage
 * - Triggering RunPod background removal processing
 * - Updating overlay with processed video storage ID
 * - Handling toggle state between original and bg-removed video
 */

import { useState, useCallback, useEffect } from 'react';
import * as React from 'react';
import { useMutation, useAction, useQuery, useConvex } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Id } from '../../../../convex/_generated/dataModel';
import { ClipOverlay } from '../types';
import { findMediaBySrc } from '../utils/general/indexdb';

interface UseBackgroundRemovalOptions {
  /** Project ID for the current project (optional, will try to get from URL if not provided) */
  projectId?: string | undefined;
  /** Current overlay being processed */
  overlay: ClipOverlay;
  /** Callback to update the overlay */
  onOverlayUpdate: (updates: Partial<ClipOverlay>) => void;
}

/**
 * Computes MD5 hash of a file/blob
 */
async function computeFileHash(file: File | Blob): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  // Convert to hex string and take first 32 chars for MD5-like format
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  // Return first 32 characters (MD5 length) for compatibility
  return hashHex.substring(0, 32);
}

/**
 * Hook to handle background removal for video overlays
 */
export function useBackgroundRemoval({
  projectId,
  overlay,
  onOverlayUpdate,
}: UseBackgroundRemovalOptions) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | undefined>(undefined);

  const convex = useConvex();

  // Check if the overlay's video is still uploading
  useEffect(() => {
    const checkUploadStatus = async () => {
      if (!overlay.src) {
        setIsVideoUploading(false);
        setUploadStatus(undefined);
        return;
      }

      // Only check blob URLs - Convex URLs are already uploaded
      if (!overlay.src.startsWith('blob:')) {
        setIsVideoUploading(false);
        setUploadStatus('completed');
        return;
      }

      try {
        const mediaItem = await findMediaBySrc(overlay.src);
        if (mediaItem) {
          const isPending =
            mediaItem.uploadStatus === 'pending' ||
            mediaItem.uploadStatus === 'uploading';
          setIsVideoUploading(isPending);
          setUploadStatus(mediaItem.uploadStatus);
        } else {
          setIsVideoUploading(false);
          setUploadStatus(undefined);
        }
      } catch (err) {
        console.error('[BackgroundRemoval] Error checking upload status:', err);
        setIsVideoUploading(false);
      }
    };

    checkUploadStatus();

    // Poll every 2 seconds while potentially uploading
    const intervalId = setInterval(checkUploadStatus, 2000);
    return () => clearInterval(intervalId);
  }, [overlay.src]);
  const generateUploadUrl = useMutation(api.projects.generateUploadUrl);
  const startOverlayRemoveBgWorker = useAction(api.projectsActions.startOverlayRemoveBgWorker);

  // Get projectId from URL if not provided
  const getProjectId = useCallback((): string | undefined => {
    if (projectId) return projectId;
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      return searchParams.get('projectId') || undefined;
    }
    return undefined;
  }, [projectId]);

  const currentProjectId = getProjectId();
  
  // Compute file hash from overlay src (will be computed when needed)
  const [fileHash, setFileHash] = useState<string | null>(null);
  
  // Track the storage ID we want to fetch URL for
  const [activeStorageId, setActiveStorageId] = useState<Id<"_storage"> | null>(
    overlay.removeBgStorageId ? (overlay.removeBgStorageId as Id<"_storage">) : null
  );
  
  // Query background removal task by file hash (for deduplication)
  const backgroundRemovalTask = useQuery(
    api.projects.getBackgroundRemovalByHash,
    fileHash ? { fileHash } : "skip"
  );

  // Query overlay-specific background removal status
  const overlayRemoveBgStatus = useQuery(
    api.projects.getOverlayRemoveBgStatus,
    currentProjectId ? {
      projectId: currentProjectId as Id<'projects'>,
      overlayId: overlay.id.toString(),
    } : "skip"
  );

  const existingProcessedStorageId = useQuery(
    api.projects.getOverlayRemoveBgStorageId,
    currentProjectId ? {
      projectId: currentProjectId as Id<'projects'>,
      overlayId: overlay.id.toString(),
    } : "skip"
  );

  // Get the processed video URL from storage ID when available
  const processedVideoUrl = useQuery(
    api.projects.getVideoUrl,
    activeStorageId ? {
      videoFileId: activeStorageId,
    } : "skip"
  );

  /**
   * Downloads a video from an external URL
   */
  const downloadVideo = useCallback(async (url: string): Promise<Blob> => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download video: ${response.statusText}`);
    }
    return await response.blob();
  }, []);

  /**
   * Uploads a video file to Convex storage
   */
  const uploadVideoToConvex = useCallback(async (file: File | Blob, fileName: string = 'video.mp4'): Promise<string> => {
    try {
      // Get upload URL
      const uploadUrl = await generateUploadUrl();
      
      // Upload the file
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': file instanceof File ? file.type : 'video/mp4',
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`Upload failed: ${uploadResponse.status} ${errorText}`);
      }

      // Get storage ID from response
      const responseText = await uploadResponse.text();
      let response;
      try {
        response = JSON.parse(responseText);
      } catch {
        // If response is not JSON, it might be just the storageId as text
        response = { storageId: responseText.trim() };
      }

      const storageId = response.storageId || response.id || response.storage_id;
      if (!storageId || typeof storageId !== 'string') {
        throw new Error('Upload response missing storageId');
      }

      return storageId;
    } catch (err) {
      console.error('Error uploading video to Convex:', err);
      throw err;
    }
  }, [generateUploadUrl]);

  /**
   * Handles enabling background removal
   * Now uses fileHash for asset-based tracking
   */
  const enableBackgroundRemoval = useCallback(async () => {
    const currentProjectId = getProjectId();
    if (!currentProjectId) {
      setError('Project ID is required');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Store original src if not already stored
      const originalSrc = overlay.originalSrc || overlay.src;

      // Get video file - either from blob URL or download from external URL
      let videoFile: File | Blob;
      let fileName = 'video.mp4';

      if (overlay.src.startsWith('blob:') || overlay.src.startsWith('data:')) {
        // For blob/data URLs, fetch and convert to blob (regardless of file format, including .mov)
        const response = await fetch(overlay.src);
        videoFile = await response.blob();
      } else if (overlay.src.startsWith('http://') || overlay.src.startsWith('https://')) {
        // For external URLs, download file (any video type, including .mov)
        videoFile = await downloadVideo(overlay.src);
        // Try to extract filename from URL and preserve extension (including .mov)
        try {
          const urlObj = new URL(overlay.src);
          const pathParts = urlObj.pathname.split('/');
          fileName = pathParts[pathParts.length - 1] || 'video.mp4';
          // If fileName does not look like a video file, default to .mp4
          if (!/\.(mp4|webm|mov|mkv|avi|m4v)$/i.test(fileName)) {
            fileName = 'video.mp4';
          }
        } catch {
          fileName = 'video.mp4';
        }
      } else if (
        overlay.src.endsWith('.mov') ||
        overlay.src.endsWith('.MOV')
      ) {
        // If for some reason it's a .mov local file path, try to fetch as a blob (rare)
        try {
          const response = await fetch(overlay.src);
          videoFile = await response.blob();
          fileName = 'video.mov';
        } catch (e) {
          throw new Error('Failed to fetch .mov video');
        }
      } else {
        // Try fetching as a last resort (supporting further custom/new protocols)
        try {
          const response = await fetch(overlay.src);
          videoFile = await response.blob();
          fileName = 'video.mov'; // Fallback
        } catch (err) {
          throw new Error('Unsupported video source type');
        }
      }

      // Compute file hash
      console.log('[BackgroundRemoval] Computing file hash...');
      const computedHash = await computeFileHash(videoFile);
      
      setFileHash(computedHash);
      console.log('[BackgroundRemoval] File hash computed:', computedHash);

      // Check if there's already a processed video for this hash using direct query
      // This is more reliable than using the reactive query which may not have updated yet
      console.log('[BackgroundRemoval] Checking for existing processed video by hash...');
      const existingTaskByHash = await convex.query(api.projects.getBackgroundRemovalByHash, { fileHash: computedHash });
      
      // Also check by overlay-specific queries
      const existingTask = existingTaskByHash || backgroundRemovalTask;
      const existingStorageId = existingTask?.processedStorageId || overlay.removeBgStorageId || existingProcessedStorageId;
      const currentStatus = existingTask?.status || overlayRemoveBgStatus?.status;
      
      console.log('[BackgroundRemoval] Existing task found:', existingTask ? 'Yes' : 'No');
      console.log('[BackgroundRemoval] Existing storage ID:', existingStorageId || 'None');
      console.log('[BackgroundRemoval] Current status:', currentStatus || 'None');

      // If task exists and is completed, use existing processed video
      if (existingStorageId && currentStatus === 'completed') {
        console.log('[BackgroundRemoval] Using existing bg-removed video:', existingStorageId);
        
        // Set active storage ID to fetch URL (if not already set)
        if (activeStorageId !== existingStorageId) {
          setActiveStorageId(existingStorageId as Id<'_storage'>);
        }
        
        onOverlayUpdate({
          removeBgEnabled: true,
          removeBgStorageId: existingStorageId,
          originalSrc: originalSrc,
        });
        
        setIsProcessing(false);
        return;
      }

      // If processing is in progress, show processing state
      if (currentStatus === 'processing') {
        console.log('[BackgroundRemoval] Background removal already in progress');
        onOverlayUpdate({
          removeBgEnabled: true,
          removeBgStorageId: existingStorageId || undefined,
          originalSrc: originalSrc,
        });
        setIsProcessing(true); // Keep processing state
        return;
      }

      // If failed, allow retry by continuing with upload
      if (currentStatus === 'failed') {
        console.log('[BackgroundRemoval] Previous attempt failed, retrying...');
      }

      // Get or upload video to Convex
      let storageId: string;

      // Check if video is already on Convex with a known storage ID
      if (overlay.mediaStorageId) {
        console.log(`[BackgroundRemoval] Video already on Convex, using existing storage ID: ${overlay.mediaStorageId}`);
        storageId = overlay.mediaStorageId;
      } else {
        // Upload video to Convex
        console.log('[BackgroundRemoval] Uploading video to Convex...');
        storageId = await uploadVideoToConvex(videoFile, fileName);
        console.log('[BackgroundRemoval] Video uploaded, storage ID:', storageId);
      }

      console.log('═══════════════════════════════════════════════════════');
      console.log('🚀 RunPod Background Removal - Starting Sync Processing');
      console.log('═══════════════════════════════════════════════════════');
      console.log('📦 Storage ID:', storageId);
      console.log('🔐 File Hash:', computedHash);
      console.log('📁 Project ID:', currentProjectId);
      console.log('🎬 Overlay ID:', overlay.id.toString());
      console.log('═══════════════════════════════════════════════════════\n');

      // Trigger RunPod processing with /runsync (blocks until complete)
      try {
        const result = await startOverlayRemoveBgWorker({
          projectId: currentProjectId as Id<'projects'>,
          overlayId: overlay.id.toString(),
          videoFileId: storageId as Id<'_storage'>,
          fileHash: computedHash,
        });

        console.log('[BackgroundRemoval] RunPod processing completed:', result);

        if (result.status === 'completed' && result.processedStorageId) {
          // Set active storage ID to fetch URL
          setActiveStorageId(result.processedStorageId);
          
          // Update overlay with processed video storage ID
          // The URL will be fetched and set in the useEffect below
          onOverlayUpdate({
            removeBgEnabled: true,
            removeBgStorageId: result.processedStorageId,
            originalSrc: originalSrc,
          });
          console.log('[BackgroundRemoval] ✅ Background removal completed successfully!');
          console.log('[BackgroundRemoval] Processed storage ID:', result.processedStorageId);
        } else if (result.status === 'failed') {
          setError(result.error || 'Background removal failed');
          console.error('[BackgroundRemoval] ❌ Background removal failed:', result.error);
        }
      } catch (runpodError) {
        console.error('[BackgroundRemoval] RunPod processing error:', runpodError);
        setError(runpodError instanceof Error ? runpodError.message : 'Failed to process background removal');
      }

    } catch (err) {
      console.error('[BackgroundRemoval] Error enabling background removal:', err);
      setError(err instanceof Error ? err.message : 'Failed to enable background removal');
    } finally {
      setIsProcessing(false);
    }
  }, [currentProjectId, convex, backgroundRemovalTask, overlayRemoveBgStatus, existingProcessedStorageId, activeStorageId, overlay, onOverlayUpdate, downloadVideo, uploadVideoToConvex, startOverlayRemoveBgWorker, getProjectId]);

  // Update overlay src when processed video URL is available
  React.useEffect(() => {
    if (processedVideoUrl && overlay.removeBgEnabled && activeStorageId) {
      console.log('[BackgroundRemoval] Updating overlay src to processed video URL:', processedVideoUrl);
      onOverlayUpdate({
        src: processedVideoUrl,
      });
    }
  }, [processedVideoUrl, overlay.removeBgEnabled, activeStorageId, onOverlayUpdate]);

  // Update processing state and overlay based on database status (for page refreshes or reactive updates)
  React.useEffect(() => {
    const status = backgroundRemovalTask?.status || overlayRemoveBgStatus?.status;
    const processedStorageId = backgroundRemovalTask?.processedStorageId || existingProcessedStorageId;
    
    if (status === 'processing') {
      setIsProcessing(true);
    } else if (status === 'completed') {
      setIsProcessing(false);
      setError(null);
      
      // If we have a processed storage ID and the overlay is enabled, set it to fetch the URL
      if (processedStorageId && overlay.removeBgEnabled && overlay.removeBgStorageId !== processedStorageId) {
        setActiveStorageId(processedStorageId as Id<'_storage'>);
        onOverlayUpdate({
          removeBgEnabled: true,
          removeBgStorageId: processedStorageId,
          originalSrc: overlay.originalSrc || overlay.src,
        });
      }
    } else if (status === 'failed') {
      setIsProcessing(false);
      const errorMsg = backgroundRemovalTask?.error || overlayRemoveBgStatus?.error || 'Background removal processing failed. Please try again.';
      setError(errorMsg);
    }
  }, [backgroundRemovalTask, overlayRemoveBgStatus, existingProcessedStorageId, overlay.removeBgEnabled, overlay.removeBgStorageId, overlay.originalSrc, overlay.src, onOverlayUpdate]);

  /**
   * Handles disabling background removal
   */
  const disableBackgroundRemoval = useCallback(() => {
    const originalSrc = overlay.originalSrc || overlay.src;
    onOverlayUpdate({
      removeBgEnabled: false,
      src: originalSrc,
    });
  }, [overlay, onOverlayUpdate]);

  // Get current status for display
  const status = backgroundRemovalTask?.status || overlayRemoveBgStatus?.status;
  const statusMessage = status === 'processing' 
    ? 'Processing video...' 
    : status === 'failed' 
    ? (backgroundRemovalTask?.error || overlayRemoveBgStatus?.error || 'Processing failed')
    : status === 'completed' 
    ? 'Background removed successfully' 
    : null;

  return {
    enableBackgroundRemoval,
    disableBackgroundRemoval,
    isProcessing: isProcessing || status === 'processing',
    error,
    status,
    statusMessage,
    // Upload status - used to disable background removal while video is uploading
    isVideoUploading,
    uploadStatus,
  };
}

